import { authFetch } from "../utils/authFetch";
import { baseUrl } from "../utils/requestUrl";


// GET /scores answers with { headings, questionAnswers }: an answer belongs to
// the assessment, not to any one subheading, so the answers sit at the root
// instead of being repeated under every score the entry recorded. It used to
// answer with the bare heading list.
//
// Normalised into one shape here so every caller keeps reading `data` as the
// headings, with the answers alongside them. The array branch keeps an older
// API answering the old way working.
const toSummaryResponse = (json) => {
  const payload = json?.data;
  const isLegacyShape = Array.isArray(payload);
  return {
    ...json,
    data: isLegacyShape ? payload : payload?.headings || [],
    questionAnswers: isLegacyShape ? [] : payload?.questionAnswers || [],
  };
};

export const fetchLatestFunctionalScores = async (patientId) => {
  const response = await authFetch(`${baseUrl}/scores/latest?patientId=${patientId}`);
  return response.json();
};

export const fetchFunctionalSummaries = async (
  patient_id,
  startDate = null,
  endDate = null,
  scores = null,
  providerIds = null,
  functionalIssues = null,
  fromDate = null,
  toDate = null,
  includeHistory = true
) => {
  let url = `${baseUrl}/scores?patientId=${patient_id}&status=SIGNED&includeHistory=${includeHistory}`;
  // Add date parameters if provided
  if (startDate && endDate) {
    const rangeStart = fromDate || startDate;
    const rangeEnd = toDate || endDate;
    url += `&startDate=${startDate}&fromDate=${rangeStart}`;
    url += `&endDate=${endDate}&toDate=${rangeEnd}`;
  }
  // If no dates provided, the API will return all data and we'll calculate min/max from the response

  // Add score parameters if provided
  if (scores && scores.length > 0) {
    url += `&scores=${scores.join(",")}`;
  }

  // Add provider parameters if provided
  if (providerIds && providerIds.length > 0) {
    url += `&providerIds=${providerIds.join(",")}`;
  }

  // Add functional issues parameters if provided
  if (functionalIssues && functionalIssues.length > 0) {
    url += `&subheadingIds=${functionalIssues.join(",")}`;
  }

  const response = await authFetch(url);

  return toSummaryResponse(await response.json());
};


export const fetchFunctionalSummariesWithTeamScores= async (
  patient_id,
  startDate = null,
  endDate = null,
  scores = null,
  providerIds = null,
  functionalIssues = null,
  fromDate = null,
  toDate = null
) => {
  let url = `${baseUrl}/scores?patientId=${patient_id}&status=SIGNED&includeHistory=true`;
  // Add date parameters if provided
  if (startDate && endDate) {
    const rangeStart = fromDate || startDate;
    const rangeEnd = toDate || endDate;
    url += `&startDate=${startDate}&fromDate=${rangeStart}`;
    url += `&endDate=${endDate}&toDate=${rangeEnd}`;
    url += `&includeTeamScore=true`;
  }
  // If no dates provided, the API will return all data and we'll calculate min/max from the response

  // Add score parameters if provided
  if (scores && scores.length > 0) {
    url += `&scores=${scores.join(",")}`;
  }

  // Add provider parameters if provided
  if (providerIds && providerIds.length > 0) {
    url += `&providerIds=${providerIds.join(",")}`;
  }

  // Add functional issues parameters if provided
  if (functionalIssues && functionalIssues.length > 0) {
    url += `&subheadingIds=${functionalIssues.join(",")}`;
  }

  const response = await authFetch(url);

  return toSummaryResponse(await response.json());
};

export const addContributorScore = async ({ data, checklistId }) => {
  const response = await fetch(`${baseUrl}/functional_issues/${checklistId}`, {
    method: "PUT",
    body: JSON.stringify(data),
    headers: {
      "Content-type": "Application/json",
    },
  });

  return response.json();
};

export const submitTeamScores = async (scoresData) => {
  const response = await authFetch(`${baseUrl}/scores/team/batch`, {
    method: "POST",
    body: JSON.stringify(scoresData),
  });

  return response.json();
};

export const fetchTeamScoresSummary = async (patient_id, startDate = null, endDate = null, providerIds = null) => {
  let url = `${baseUrl}/scores/team/batches/${patient_id}`;
  
  // Build query parameters
  const params = new URLSearchParams();
  
  // Add date parameters if provided
  if (startDate) {
    params.append('startDate', startDate);
  }
  if (endDate) {
    params.append('endDate', endDate);
  }
  
  // Add provider parameters if provided
  if (providerIds && providerIds.length > 0) {
    params.append('providerIds', providerIds.join(','));
  }
  
  // Add query parameters to URL if any exist
  if (params.toString()) {
    url += `?${params.toString()}`;
  }
  
  const response = await authFetch(url);
  const data = await response.json();
  return data;
};

export const fetchFunctionalSummaryGraph = async (
  patient_id,
  startDate = null,
  endDate = null,
  scores = null,
  providerIds = null,
  functionalIssues = null
) => {
  let url = `${baseUrl}/scores/graph/${patient_id}`;

  // Build query parameters
  const params = new URLSearchParams();

  // Add date parameters if provided
  if (startDate && endDate) {
    params.append('startDate', startDate);
    params.append('fromDate', startDate);
    params.append('endDate', endDate);
    params.append('toDate', endDate);
  }

  // Add score parameters if provided
  if (scores && scores.length > 0) {
    params.append('scores', scores.join(','));
  }

  // Add provider parameters if provided
  if (providerIds && providerIds.length > 0) {
    params.append('providerIds', providerIds.join(','));
  }

  // Add functional issues parameters if provided
  if (functionalIssues && functionalIssues.length > 0) {
    params.append('subheadingIds', functionalIssues.join(','));
  }

  // Add query parameters to URL if any exist
  if (params.toString()) {
    url += `?${params.toString()}`;
  }

  const response = await authFetch(url);

  return response.json();
};
